import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import {
  AssetState,
  CHECKOUT_MATOMO_EVENT_CATEGORY_PURCHASE,
  FlowState,
  PersonalizationPerson,
  ProductState,
  Purchase,
  Renew,
  SelectCountry,
  SelectLicenseType,
  ShoppingCartBillingState,
  ShoppingCartPaymentState,
  ShoppingCartPersonalizationState,
  ShoppingCartService,
  ShoppingCartState,
  WalletPaymentData
} from '@platform-frontend/checkout';
import {
  BillingAddress,
  BusinessModel,
  CountryState,
  isReusablePaymentMethod,
  LicenseType,
  MARKETPLACE_MATOMO_EVENT_CATEGORY_BILLING,
  MARKETPLACE_MATOMO_EVENT_CATEGORY_INTERVAL,
  PaymentMethodInfo,
  PricingInformation,
  ProductCategory,
  ProductLocalization,
  WalletPaymentMethodInfo
} from '@platform-frontend/marketplace';
import { PLATFORM_ENVIRONMENT, YATTA_PRIVACY_POLICY_TEMPLATE_URL, YATTA_TERMS_TEMPLATE_SUBSCRIPTION_URL } from '@platform-frontend/platform';
import { InclusiveActionFilter } from '@platform-frontend/ui-tools';
import { ClearNotifications, Navigate, tapNextOrError } from '@platform-frontend/util';
import { MatomoTracker } from 'ngx-matomo-client';
import { BehaviorSubject, combineLatest, filter, map, Observable, of, Subject, switchMap, take, tap } from 'rxjs';
@UntilDestroy()
@Component({
  selector: 'yat-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss'],
  providers: [ShoppingCartService]
})
export class OverviewComponent implements OnInit {
  constructor(
    private activatedRoute: ActivatedRoute,
    private shoppingCartService: ShoppingCartService,
    private store: Store,
    private matomoTracker: MatomoTracker
  ) {}

  subscribeActionFilter = new InclusiveActionFilter(Purchase, Renew);

  @Select(ProductState.billingAddressRequired)
  billingAddressRequired$: Observable<boolean>;

  @Select(CountryState.countries)
  countries$: Observable<string[]>;

  @Select(ProductState.commercialLicenseTypes)
  commercialLicenseTypes$: Observable<LicenseType[]>;

  @Select(ShoppingCartBillingState.selectedCountry)
  selectedCountry$: Observable<string>;

  @Select(ShoppingCartBillingState.selectedBillingAddress)
  selectedBillingAddress$: Observable<BillingAddress | null>;

  @Select(ShoppingCartPaymentState.selectedPaymentMethod)
  selectedPaymentMethod$: Observable<PaymentMethodInfo | null>;

  @Select(ShoppingCartState.selectedLicenseType)
  selectedLicenseType$: Observable<LicenseType | null>;

  @Select(ShoppingCartState.pricingInformation)
  pricingInformation$: Observable<PricingInformation | 'LOADING' | null>;

  @Select(AssetState.endOfPeriod)
  endOfPeriod$: Observable<Date | null>;

  @Select(FlowState.isOrderToBeManaged)
  isOrderToBeManaged$: Observable<boolean>;

  @Select(ProductState.isLive)
  isLive$: Observable<boolean>;

  @Select(ProductState.vendorName)
  vendorName$: Observable<string | null>;

  @Select(ProductState.productName)
  productName$: Observable<string | null>;

  @Select(ProductState.productCategory)
  productCategory$: Observable<ProductCategory | null>;

  @Select(ProductState.productLocalization)
  productLocalization$: Observable<ProductLocalization | null>;

  @Select(ProductState.fixedFulfillmentCountry)
  fixedFulfillmentCountry$: Observable<boolean | null>;

  @Select(ProductState.businessModel)
  businessModel$: Observable<BusinessModel | null>;

  @Select(ProductState.isPersonalizationRequired)
  isPersonalizationRequired$: Observable<boolean>;

  @Select(ShoppingCartPersonalizationState.allPersons)
  allPersons$: Observable<PersonalizationPerson<string | null>[]>;

  @Select(ShoppingCartPersonalizationState.canPersonsBeAdded)
  canPersonsBeAdded$: Observable<boolean>;

  @Select(ShoppingCartPersonalizationState.arePersonsSet)
  arePersonsSet$: Observable<boolean>;

  readonly isProduction = PLATFORM_ENVIRONMENT.isProduction;

  purchaseButtonEnabled$: Observable<boolean> = of(false);
  private purchaseOngoing$: Subject<boolean> = new BehaviorSubject<boolean>(false);

  YATTA_TERMS_TEMPLATE_URL = YATTA_TERMS_TEMPLATE_SUBSCRIPTION_URL;
  YATTA_PRIVACY_POLICY_TEMPLATE_URL = YATTA_PRIVACY_POLICY_TEMPLATE_URL;
  readonly MATOMO_EVENT_CATEGORY_PURCHASE = CHECKOUT_MATOMO_EVENT_CATEGORY_PURCHASE;
  readonly MATOMO_EVENT_CATEGORY_INTERVAL = MARKETPLACE_MATOMO_EVENT_CATEGORY_INTERVAL;
  readonly MATOMO_EVENT_CATEGORY_BILLING = MARKETPLACE_MATOMO_EVENT_CATEGORY_BILLING;

  ngOnInit(): void {
    this.purchaseButtonEnabled$ = combineLatest([
      this.billingAddressRequired$,
      this.selectedBillingAddress$,
      this.selectedPaymentMethod$,
      this.shoppingCartService.getPersonalizationAdditionStatus(),
      this.isPersonalizationRequired$
    ]).pipe(
      map(
        ([billingAddressRequired, selectedBillingAddress, selectedPaymentMethod, personalizationAdditionStatus, isPersonalizationRequired]) =>
          selectedPaymentMethod !== null &&
          !(billingAddressRequired && selectedBillingAddress === null) &&
          !(isPersonalizationRequired && personalizationAdditionStatus !== 'COMPLETE')
      )
    );
  }

  selectCountry(countryCode: string): void {
    this.store.dispatch(new ClearNotifications());
    this.store.dispatch(new SelectCountry(countryCode));
    this.matomoTracker.trackEvent(this.MATOMO_EVENT_CATEGORY_BILLING, 'Modify_NoBillingAddressIfNoCountry', countryCode);
  }

  switchSelectedPaymentMethod(): void {
    this.store.dispatch(new Navigate(['../payment-method-selection'], { relativeTo: this.activatedRoute }));
  }

  createBillingAddress(): void {
    this.store.dispatch(new Navigate(['../billing-address'], { relativeTo: this.activatedRoute }));
  }

  switchSelectedBillingAddress(): void {
    this.store.dispatch(new Navigate(['../billing-address-selection'], { relativeTo: this.activatedRoute }));
  }

  selectLicenseType(licenseTypeId: string | null): void {
    if (licenseTypeId) {
      this.store.dispatch(new ClearNotifications());
      this.store.dispatch(new SelectLicenseType(licenseTypeId));
      this.matomoTracker.trackEvent(this.MATOMO_EVENT_CATEGORY_INTERVAL, 'SubscriptionPeriod', licenseTypeId);
    }
  }

  tryPurchaseOrRenew(paymentWalletData?: WalletPaymentData): void {
    this.purchaseOngoing$
      .pipe(
        take(1),
        filter(ongoing => !ongoing),
        tap(() => this.purchaseOngoing$.next(true)),
        switchMap(() => this.shoppingCartService.tryPurchaseOrRenewAndNavigateTo('../confirmation', paymentWalletData ?? null)),
        tapNextOrError(() => this.purchaseOngoing$.next(false)),
        untilDestroyed(this)
      )
      .subscribe();
  }

  isWalletPaymentMethodInfo(paymentMethodInfo: PaymentMethodInfo | null): paymentMethodInfo is WalletPaymentMethodInfo {
    return paymentMethodInfo !== null && !isReusablePaymentMethod(paymentMethodInfo);
  }

  personCardSelected(populateInput: boolean = false): void {
    const maxNumberOfPersons: number = this.store.selectSnapshot(ProductState.maxNumberOfPersons);
    const personalizationDataSet: boolean = this.store.selectSnapshot(ShoppingCartPersonalizationState.arePersonsSet);
    const route = maxNumberOfPersons > 1 && personalizationDataSet ? '../multiple-person' : '../create-person';
    this.store.dispatch(new Navigate([route], { relativeTo: this.activatedRoute, queryParams: { populateInput } }));
  }
}
